import { Component, OnInit, OnDestroy, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Person, EditPerson } from '../../interfaces/persons.interface';
import { PersonsService } from '../../services/persons.service';
import { Subject, takeUntil } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { UsersService } from '../../services/users.service';
import { PwaService } from '../../services/pwa.service';

@Component({
  selector: 'app-person-edit-data',
  standalone: false,
  templateUrl: './person-edit-data.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./person-edit-data.component.scss']
})
export class PersonEditDataComponent implements OnInit, OnDestroy {
  @Output() personChanged = new EventEmitter<void>();

  person_id: number | null = null;
  person: Person | null = null;
  loading = false;
  selectedTabIndex = 0;
  isNewPerson = false;
  isAcceptedPolicy = false;
  acceptForm!: FormGroup;
  private $destroy = new Subject<void>();

  constructor(
    private personsService: PersonsService,
    private usersService: UsersService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private pwaService: PwaService
  ) { }

  ngOnInit(): void {

    // Initialize accept form
    this.acceptForm = this.fb.group({
      acceptPolicy: [false, Validators.requiredTrue]
    });

      //this.isAcceptedPolicy = this.usersService.currentUser?.accept_policy || false;
      this.isAcceptedPolicy = true;

    // Initialize form value based on current user
    if (this.isAcceptedPolicy) {
      this.acceptForm.patchValue({
        acceptPolicy: false
      });
    }
    // Get person ID from route parameters
    this.route.params.pipe(takeUntil(this.$destroy)).subscribe(params => {
      this.person_id = params['id'] ? parseInt(params['id'], 10) : null;
      this.isNewPerson = !this.person_id;

      if (this.person_id) {
        this.loadPersonData();
      } else {
        // Initialize empty person for new person creation
        this.person = {
          id: 0,
          name: '',
          surname: '',
          type: '',
          phone: '',
          cards: [],
          user: {} as any
        } as Person;
      }
    });
  }

  ngOnDestroy(): void {
    this.$destroy.next();
    this.$destroy.complete();
  }

  loadPersonData(isChildren = 0): void {
    this.loading = true;
    this.personsService.getData(this.person_id)
      .pipe(takeUntil(this.$destroy))
      .subscribe({
        next: (data: Person) => {
          this.person = data;
          this.loading = false;
          if (!isChildren) {
            this.onCheckUserType(this.person as unknown as EditPerson);
          } else {
             setTimeout(() => {
              this.selectedTabIndex = 2; // "Twoje Karty" tab index
            }, 1000);// "Rodzina" tab index
          }

          this.personChanged.emit();
        },
        error: (err) => {
          console.error('Error loading person data:', err);
          this.loading = false;
          this.snackBar.open('Nie udało się pobrać danych mieszkańca.', 'Zamknij', {
            duration: 12000,
            panelClass: ['error-snackbar'],
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });
        }
      });
  }

  onChildChanged(): void {
    this.loadPersonData(1);

    // Show success message and optionally navigate back
    this.snackBar.open('Dane o dzieciach zostały zaktualizowane', 'Zamknij', {
      duration: 10000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }


  onPersonChanged(person?: EditPerson | undefined): void {
    if (person) {
      this.person_id = person.id;
    }

    // If default is set to 1, refresh person selection
    if (person?.default === 1) {
      this.personsService.getCurrent().subscribe({
        next: (persons: Person[]) => {
          this.usersService.handlePersonAndCardSelection(persons, {
            next: () => {
              /*
              this.snackBar.open('Zmieniono wyświetlana katę dla wyznaczonej osoby. (W przypadku gdy osoba nie ma jeszcze aktywnej karty, ustawiana jest pierwsza aktywna karta dla konta użytkownika.', 'Zamknij', {
                duration: 5000,
                panelClass: ['success-snackbar'],
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
              });
              */
              this.loadPersonData();

            },
            error: () => {
              /*
              this.snackBar.open('Błąd podczas ustawianie domyslnej karty mieszkańca', 'Zamknij', {
                duration: 7000,
                panelClass: ['error-snackbar'],
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
              });
              */
            },
            complete: () => { }
          });
        },
        error: () => {
          this.snackBar.open('Błąd podczas odczytu zmienionych danych', 'Zamknij', {
            duration: 7000,
            panelClass: ['error-snackbar'],
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });
        }
      });
    } else {
      this.loadPersonData();

    }



    
    // Show success message and optionally navigate back
    this.snackBar.open('Dane wniosku zostały zaktualizowane', 'Zamknij', {
      duration: 13000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }

  goBackToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  onPolicyAcceptanceChange(): void {
    if (this.acceptForm.invalid) return;

    const acceptPolicy = this.acceptForm.get('acceptPolicy')?.value;

    this.usersService.updateCurrent({ accept_policy: acceptPolicy }).subscribe({
      next: () => {
        // Update local user object
        if (this.usersService.currentUser) {
          this.usersService.currentUser.accept_policy = acceptPolicy;
          this.usersService.setCurrentUser(this.usersService.currentUser);
        }
        this.isAcceptedPolicy = acceptPolicy;
        
        this.snackBar.open('Polityka prywatności została zaakceptowana', 'Zamknij', {
          duration: 8000,
          panelClass: ['success-snackbar'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });

        // Recheck PWA install eligibility after policy acceptance
        this.pwaService.recheckEligibility();

        // Reload person data
        if (this.person_id) {
          this.loadPersonData();
        }
      },
      error: (err: any) => {
        let errorMessage = 'Wystąpił błąd podczas zapisywania!';
        if (err.status === 400) {
          errorMessage = 'Błędne dane.';
        } else if (err.status === 500) {
          errorMessage = 'Błąd serwera. Spróbuj ponownie później.';
        }

        this.snackBar.open(errorMessage, 'Zamknij', {
          duration: 20000,
          panelClass: ['error-snackbar'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });

        // Revert checkbox on error
        this.acceptForm.patchValue({
          acceptPolicy: this.isAcceptedPolicy
        });
      }
    });
  }

  onPersonSaved(person: EditPerson): void {
    // Switch to appropriate tab after saving
    setTimeout(() => {
      if (person.type === 'CHILDREN') {
        this.selectedTabIndex = 1; // "Rodzina" tab index
      } else {
        this.selectedTabIndex = 2; // "Twoje Karty" tab index
      }
    }, 300);
  }

  onCheckUserType(editPerson: EditPerson): void {
    setTimeout(() => {
      /*
      if (editPerson.type === 'CITIZEN') {
        this.selectedTabIndex = 2; // "Twoje Karty" tab index (0-based)
      } else if (editPerson.type === 'CHILDREN') {
        this.selectedTabIndex = 2; // "Rodzina" tab index (0-based)
      } else if (editPerson.type === 'SENIOR') {
        this.selectedTabIndex = 2; // "Twoje Karty" tab index
      } else if (editPerson.type === 'STUDENT') {
        this.selectedTabIndex = 2; // "Twoje Karty" tab index
      }*/
    }, 1000);
  }

  getPersonTypeLabel(type: string | undefined): string {
    const typeMap: { [key: string]: string } = {
      'CITIZEN': 'Podatnik',
      'SENIOR': 'Senior',
      'STUDENT': 'Student',
      'CHILDREN': 'Dziecko'
    };
    return type ? typeMap[type] || type : '';
  }
}
